import { useState } from 'react';
import { useToggle } from 'react-use';
import { fetchProvisionErrors as _fetchProvisionErrors } from 'thunk';
import React from 'react';
import useSandboxes from 'SandboxModule/hooks/useSandboxes';
import { useBoardsQuery } from 'src/modules/BoardModule/api/queries/useBoardsQuery';
import useTeams from 'hooks/useTeams';
import { useForceProvisionConfirmationModal } from './useForceProvisionConfirmationModal';
import { ProvisionIntegrationPayload } from 'IntegrationModule/api/provisionIntegrationApi';
import { useProvisionIntegration } from 'IntegrationModule/api/queries/useProvisionIntegration';
import { useCreateGrafanaDatasource } from './useCreateGrafanaDatasource';

export interface IntegrationProvisionInputValues {
  databaseName: string;
  password: string;
  apiKey: string;
  showPassword: boolean;
  showApiKey: boolean;
  bucketName: string;
  bakIntegration: boolean;
  serverAddress: string;
  realmId: string | undefined;
  username: string;
}

export interface UseProvisionModalFieldsProps {
  teamId: number;
  targetService: string;
  formatProvisionPayload: (args: {
    inputValues: IntegrationProvisionInputValues;
  }) => Omit<ProvisionIntegrationPayload, 'mosaicTeamId' | 'mosaicTeamName' | 'targetService'>;
  /**
   * Optional tenant for API header override.
   * Used to set x-tenant-name header for multi-tenant routing.
   */
  tenant?: string;
  /**
   * Optional realmId for API header override, derived from navigation/routing context.
   * Used to set x-realm-id header for multi-tenant routing.
   * Note: This is distinct from the `realmId` field in `IntegrationProvisionInputValues`,
   * which represents the realm selected or edited via the provision form inputs.
   */
  realmId?: string;
}

export const useProvisionModalFields = ({
  teamId,
  targetService,
  formatProvisionPayload,
  tenant,
  realmId,
}: UseProvisionModalFieldsProps) => {
  const { getSelectedRailsSandbox } = useSandboxes();
  const currentSelectedRealmId = getSelectedRailsSandbox()?.realm_id;
  const defaultValues = {
    databaseName: '',
    password: '',
    apiKey: '',
    showPassword: false,
    showApiKey: false,
    bucketName: '',
    bakIntegration: false,
    serverAddress: 'prod-api.database.windows.net',
    realmId: currentSelectedRealmId,
    username: 'postgres',
  };
  const { teamHash } = useTeams();
  const mosaicTeamName = teamHash[teamId]?.name;
  const [inputValues, setInputValues] = useState<IntegrationProvisionInputValues>(defaultValues);
  const [showAdvancedSetting, toggleShowAdvancedSetting] = useToggle(false);
  const { openForceProvisionConfirmationModal } = useForceProvisionConfirmationModal();

  const { getDefaultBoard } = useBoardsQuery({
    shouldFetchDefaultBoard: true,
    team_id: teamId,
    tenant,
    realmId,
  });

  const { createGrafanaDatasource } = useCreateGrafanaDatasource();

  const provisionIntegrationOnSuccess = () => {
    const params = {
      databaseName: `${teamId}-${inputValues.databaseName}`,
      engine: 'mssql',
      host: inputValues.serverAddress,
      database: inputValues.databaseName,
      username: inputValues.username,
      password: inputValues.password,
    };
    createGrafanaDatasource(params);
  };

  const {
    provisionIntegration: _provisionIntegration,
    isProvisioningIntegration,
    provisionIntegrationError,
    provisionIntegrationData,
  } = useProvisionIntegration();

  const toggleFieldVisibility = (key: string) => {
    setInputValues({
      ...inputValues,
      [key]: !inputValues[key as keyof typeof inputValues],
    });
  };

  const resetServerAddress = () => {
    setInputValues({
      ...inputValues,
      serverAddress: defaultValues['serverAddress'],
    });
  };

  const togglePasswordVisibility = () => toggleFieldVisibility('showPassword');

  const toggleShowApiKey = () => toggleFieldVisibility('showApiKey');

  const handleInputChangeV2 = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValues({ ...inputValues, [event.target.name]: event.target.value });
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setInputValues({
      ...inputValues,
      [name]: checked,
    });
  };

  const handleToggleAdvanced = () => {
    toggleShowAdvancedSetting();
    setInputValues({
      ...inputValues,
      bucketName: defaultValues.bucketName,
      bakIntegration: defaultValues.bakIntegration,
      serverAddress: defaultValues.serverAddress,
      realmId: defaultValues.realmId,
    });
  };

  const provisionIntegration = ({
    //This is a FE key, not param for integration provision
    shouldIncludeBakFile = true,
    forceProvision = false,
  }: {
    shouldIncludeBakFile?: boolean;
    forceProvision?: boolean;
  } = {}) => {
    const formattedPayload = formatProvisionPayload({
      inputValues,
    });

    if (mosaicTeamName) {
      const apiParams: ProvisionIntegrationPayload = {
        ...formattedPayload,
        mosaicTeamId: teamId,
        mosaicTeamName,
        targetService,
        // Only QB doesn't have paramsOnAgent and forceProvision doesn't apply to QB
        ...(formattedPayload.paramsOnAgent && {
          paramsOnAgent: {
            ...formattedPayload.paramsOnAgent,
            forceCreate: forceProvision,
          },
        }),
        integrationSetting: {
          defaultBoardId: getDefaultBoard()?.id,
          ...formattedPayload.integrationSetting,
          ...(shouldIncludeBakFile ? { bakIntegration: inputValues.bakIntegration } : null),
        },
      };
      _provisionIntegration(apiParams, {
        onSuccess: provisionIntegrationOnSuccess,
      });
    }
  };

  const handleProvision = () => {
    provisionIntegration();
  };

  const handleForceProvision = () => {
    openForceProvisionConfirmationModal({
      onConfirm: () => {
        provisionIntegration({ forceProvision: true });
      },
    });
  };

  return {
    handleToggleAdvanced,
    toggleShowAdvancedSetting,
    handleCheckboxChange,
    handleInputChangeV2,
    toggleFieldVisibility,
    showAdvancedSetting,
    inputValues,
    togglePasswordVisibility,
    toggleShowApiKey,
    isProvisioningIntegration,
    resetServerAddress,
    provisionIntegrationError,
    provisionIntegrationData,
    mosaicTeamName,
    handleForceProvision,
    handleProvision,
    openForceProvisionConfirmationModal,
  };
};
